import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { FunctionField, SaveButton, useTranslate } from 'react-admin';
import PropTypes from 'prop-types';
import CustomEditIconButton from '@/components/global/buttons/CustomEditIconButton.jsx';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';

const getPlainTextLength = htmlString => {
    if (!htmlString) return 0;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    return doc.body.textContent.length;
};

const ServiceRequestEditField = ({ text, onOpen, isHtml = false, isFilled = false, canEdit = true, colorChoice = null, tooltipTitle = null }) => {
    const translate = useTranslate();
    const theme = useTheme();
    const isInputWide = useMediaQuery(theme.breakpoints.up('sm'));
    const textLength = isHtml ? getPlainTextLength(text) : text.length;
    const shouldShowGradient = textLength > (isInputWide ? 350 : 200);

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                color: isFilled ? 'inherit' : '#B4B4B4',
                '& .button--icon-only': {
                    padding: '6px',
                    minWidth: '28px !important',
                    borderRadius: '6px',
                    '& svg': {
                        width: '16px',
                        height: '16px',
                    },
                },
                '& p': {
                    margin: '0',
                },
            }}
        >
            <Box
                className='editable-field-content'
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    '& > div': {
                        maxHeight: '120px',
                        overflow: 'hidden',
                        position: 'relative',
                        ...(shouldShowGradient && {
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: '100%',
                                background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)',
                                pointerEvents: 'none',
                            },
                        }),
                    },
                }}
            >
                {colorChoice && (
                    <Box
                        className='color-box'
                        sx={{
                            position: 'relative',
                            width: '20px',
                            minWidth: '20px',
                            height: '20px',
                            maxHeight: '20px',
                            marginRight: '8px',
                            zIndex: 1,
                            overflow: 'visible',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            borderRadius: '4px',
                            backgroundColor: `${colorChoice.color || colorChoice.id} !important`,
                            ...(colorChoice.outlineColor && {
                                borderRadius: '6px',
                                outline: '2px solid',
                                outlineOffset: '-2px',
                                outlineColor: colorChoice.outlineColor,
                            }),
                        }}
                    >
                        {colorChoice.icon ? colorChoice.icon : null}
                    </Box>
                )}
                {isHtml ? <div dangerouslySetInnerHTML={{ __html: text }} /> : text}
            </Box>
            {canEdit && <CustomEditIconButton onOpen={onOpen} title={translate('common.edit')} tooltipTitle={tooltipTitle} sx={{ marginLeft: '5px' }} />}
        </Box>
    );
};

const EditableField = ({
    label,
    isEditing,
    onEdit,
    displayValue,
    isFilled,
    isHtml = false,
    editComponent,
    sx = {
        width: '100%',
        '& .MuiFormHelperText-root': {
            display: 'none',
        },
    },
    canEdit = true,
    colorChoice = null,
    isHorizontal = true,
    className = '',
    tooltipTitle = null,
    isDescritpionField = false,
}) => {
    const translate = useTranslate();
    return (
        <FunctionField
            label={label}
            sx={{
                ...sx,
                ...(isDescritpionField && {
                    width: '100%',
                    '& > .MuiBox-root': {
                        flexWrap: 'nowrap',
                        gap: '16px',
                    },
                    '& .RaRichTextInput-editorContent .tiptap.ProseMirror': {
                        wordBreak: 'break-all',
                        padding: '24px 24px 130px',
                        maxWidth: '100%',
                        [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                            minHeight: '170px',
                            marginBottom: '4px',
                        },
                    },
                    '& .MuiFormHelperText-root': {
                        display: 'none',
                    },
                    '& .MuiStack-root': {
                        '& .MuiToggleButtonGroup-root:nth-of-type(1)': {
                            position: 'absolute',
                            bottom: 74,
                            left: 24,
                            zIndex: 2,
                        },
                        '& .MuiToggleButtonGroup-root:nth-of-type(2)': {
                            position: 'absolute',
                            bottom: 32,
                            left: 24,
                            zIndex: 2,
                        },
                    },
                }),
                '& .MuiStack-root.ra-field': {
                    '& .RaLabeled-label': {
                        alignItems: 'center !important',
                    },
                },
            }}
            className={className}
            render={record => {
                const text = typeof displayValue === 'function' ? displayValue(record) : displayValue;
                const filled = typeof isFilled === 'function' ? isFilled(record) : isFilled;
                const currentColorChoice = typeof colorChoice === 'function' ? colorChoice(record) : colorChoice;

                return !isEditing ? (
                    <ServiceRequestEditField
                        record={record}
                        isFilled={filled}
                        text={text}
                        onOpen={onEdit}
                        isHtml={isHtml}
                        canEdit={canEdit}
                        colorChoice={currentColorChoice}
                        tooltipTitle={tooltipTitle}
                    />
                ) : (
                    <Box
                        display='flex'
                        flexDirection={isHorizontal ? 'row' : 'column'}
                        alignItems='center'
                        justifyContent='space-between'
                        gap='16px'
                        sx={{
                            [`@media (max-width: ${breakpoints.values.sm}px)`]: {
                                flexDirection: 'column',
                                gap: '4px',
                                alignItems: 'stretch',
                                '& > .MuiFormControl-root': {
                                    width: '100%',
                                },
                            },
                        }}
                    >
                        {editComponent}
                        <SaveButton
                            icon={null}
                            label={translate('common.apply')}
                            sx={{
                                marginBottom: '0',
                                alignSelf: 'flex-end',
                                padding: '10px 20px',
                                minWidth: '97px',
                                [`@media (min-width: ${breakpoints.values.sm}px)`]: {
                                    marginBottom: '0',
                                    alignSelf: isHorizontal ? 'center' : 'flex-end',
                                },
                            }}
                        />
                    </Box>
                );
            }}
        />
    );
};

EditableField.propTypes = {
    label: PropTypes.string.isRequired,
    isEditing: PropTypes.bool.isRequired,
    onEdit: PropTypes.func.isRequired,
    displayValue: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
    isFilled: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    isHtml: PropTypes.bool,
    editComponent: PropTypes.node,
    sx: PropTypes.object,
    canEdit: PropTypes.bool,
    colorChoice: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
    tooltipTitle: PropTypes.string,
    isDescritpionField: PropTypes.bool,
    isHorizontal: PropTypes.bool,
    className: PropTypes.string,
};

export default EditableField;
